import React, { useMemo, useEffect } from "react";
import { useStorefrontAdministrationConfig } from "../../../hooks/StorefrontAdministrationConfigContext";
import Box from "@mui/material/Box";
import Loading from "../../Loading";
import Button from "@mui/material/Button";
import {
  GridToolbarContainer,
  GridToolbarFilterButton,
  GridToolbarExport,
  GridToolbarDensitySelector,
} from "@mui/x-data-grid-premium";
import Paper from "@mui/material/Paper";
import TranslatableDataGrid from "../../shared/TranslatableDataGrid";
import { ThemeProvider } from "@mui/material/styles";
import StorefrontAdminTheme from "../../shared/MUIStyles/StorefrontAdminTheme";
import Typography from "@mui/material/Typography";

// Custom hooks
import { useDomesticCountries } from "./hooks/useDomesticCountries";
import { useDomesticCountryForm } from "./hooks/useDomesticCountryForm";
import { useDialogState } from "./hooks/useDialogState";

// Components
import { AddDomesticCountryDialog } from "./components/AddDomesticCountryDialog";
import { UpdateDomesticCountryDialog } from "./components/UpdateDomesticCountryDialog";
import { DeleteDomesticCountryDialog } from "./components/DeleteDomesticCountryDialog";
import { AddressTypeToggle } from "./components/AddressTypeToggle";

// Constants
import { getColumns } from "./constants";

export const DomesticCountriesAdministration = () => {
  const localized = useStorefrontAdministrationConfig().labels;
  const theme = StorefrontAdminTheme();

  // Custom hooks
  const {
    fetching,
    domesticCountries,
    updateDomesticCountry,
    addressType,
    responseMessages,
  } = useDomesticCountries(localized);

  const {
    openAdd,
    openUpdate,
    openDelete,
    selectedRow,
    handleClickOpenAdd,
    handleCloseAdd,
    handleClickOpenUpdate,
    handleCloseUpdate,
    handleClickOpenDelete,
    handleCloseDelete,
  } = useDialogState();

  const formState = useDomesticCountryForm(localized);

  useEffect(() => {
    if (openUpdate && selectedRow) {
      formState.populateForm(selectedRow);
    }
  }, [openUpdate, selectedRow]);

  useEffect(() => {
    if (openAdd) {
      formState.resetForm();
    }
  }, [openAdd]);

  useEffect(() => {
    if (openDelete) {
      formState.setDialogError("");
    }
  }, [openDelete]);

  const customToolbar = () => {
    return (
      <GridToolbarContainer>
        <AddressTypeToggle addressType={addressType} localized={localized} />

        <Box sx={{ flexGrow: 1 }} />

        <GridToolbarFilterButton
          slotProps={{
            tooltip: { title: "Filter list" },
          }}
          variant="outlined"
          color="update"
        />

        <GridToolbarDensitySelector
          slotProps={{
            tooltip: { title: "Change density" },
          }}
          variant="outlined"
          color="update"
        />

        <GridToolbarExport
          printOptions={{ disableToolbarButton: true }}
          slotProps={{
            tooltip: { title: "Export data" },
          }}
          variant="outlined"
          color="update"
        />

        <strong>
          <Button
            variant="standard"
            color="success"
            style={{ marginLeft: 16 }}
            onClick={handleClickOpenAdd}
          >
            {localized.Add}
          </Button>
        </strong>
      </GridToolbarContainer>
    );
  };

  const handleAddSubmit = async () => {
    const formData = formState.getFormData();
    const result = await updateDomesticCountry(formData, "add");
    if (result && result.isSuccess) {
      handleCloseAdd();
    } else {
      const errorMessage = result?.response
        ? responseMessages[result.response]
        : responseMessages.GenericFailure;
      formState.setDialogError(errorMessage || localized.OperationFailed);
    }
    return result?.isSuccess || false;
  };

  const handleUpdateSubmit = async () => {
    if (!selectedRow) {
      return false;
    }

    const formData = formState.getFormData(selectedRow.id);
    const result = await updateDomesticCountry(formData, "update");
    if (result && result.isSuccess) {
      handleCloseUpdate();
    } else {
      const errorMessage = result?.response
        ? responseMessages[result.response]
        : responseMessages.GenericFailure;
      formState.setDialogError(errorMessage || localized.OperationFailed);
    }
    return result?.isSuccess || false;
  };

  const handleDeleteSubmit = async () => {
    if (!selectedRow) {
      return false;
    }

    const formData = { id: selectedRow.id };
    const result = await updateDomesticCountry(formData, "delete");
    if (result && result.isSuccess) {
      handleCloseDelete();
    } else {
      const errorMessage = result?.response
        ? responseMessages[result.response]
        : responseMessages.GenericFailure;
      formState.setDialogError(errorMessage || localized.OperationFailed);
    }
    return result?.isSuccess || false;
  };

  const columns = useMemo(
    () => getColumns(localized, handleClickOpenUpdate, handleClickOpenDelete),
    [localized]
  );

  const domesticCountriesItems = useMemo(() => {
    return (
      <Paper
        sx={{
          height: "calc(100vh - 200px)",
          minHeight: "420px",
          width: "100%",
          display: "flex",
          flexDirection: "column",
          marginBottom: "2rem",
        }}
      >
        <TranslatableDataGrid
          rows={domesticCountries}
          columns={columns}
          insetToolbar={customToolbar}
          labels={localized}
          sx={{
            flex: 1,
            "& .MuiDataGrid-root": {
              height: "100%",
            },
          }}
        />
      </Paper>
    );
  }, [domesticCountries, columns, localized]);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
      }}
    >
      {fetching && <Loading type="brand" />}
      {!fetching && localized && Object.keys(localized).length > 0 && (
        <>
          <ThemeProvider theme={theme}>
            <Box
              sx={{
                flexShrink: 0,
                padding: 2,
                paddingBottom: 1,
              }}
            >
              <Typography variant="h1" sx={{ marginBottom: 1 }}>
                {localized.DomesticCountriesAdministrationHeader}
              </Typography>
            </Box>

            <Box
              sx={{
                flex: 1,
                padding: 2,
                paddingTop: 0,
              }}
            >
              {domesticCountriesItems}
            </Box>

            {/* Dialogs */}
            <AddDomesticCountryDialog
              open={openAdd}
              onClose={handleCloseAdd}
              onSubmit={handleAddSubmit}
              formState={formState}
              localized={localized}
            />

            <UpdateDomesticCountryDialog
              open={openUpdate}
              onClose={handleCloseUpdate}
              onSubmit={handleUpdateSubmit}
              formState={formState}
              localized={localized}
            />

            <DeleteDomesticCountryDialog
              open={openDelete}
              onClose={handleCloseDelete}
              onSubmit={handleDeleteSubmit}
              selectedRow={selectedRow}
              localized={localized}
              dialogError={formState.dialogError}
            />
          </ThemeProvider>
        </>
      )}

      {!fetching && (!localized || Object.keys(localized).length === 0) && (
        <Typography variant="h6" color="error">
          {localized.AccessDeniedDomesticCountries}
        </Typography>
      )}
    </Box>
  );
};

export default DomesticCountriesAdministration;
